// The browser frame around the mobile app (docs/PLAN_web-app.md §4.2).
//
// Three layouts, chosen from the real viewport width:
//
//   phone   < 768px    the app, full width, exactly as on a phone
//   tablet  768–1023   a centred 600px canvas; the app's own bottom nav stays
//   desktop ≥ 1024     logged out: a 640px column on a ground that matches
//                      the screen; the screen's hero merges into it and its
//                      ornament spans the whole window
//                      (OrnamentBackground.web.tsx), so the sheet reads as a
//                      card on a web page. Logged in: a left sidebar and an
//                      880px content column on the same kind of ground.
//
// The page scrolls in every frame (DocScrollView.tsx); the phone's bottom
// bar is pinned to the window (BottomNavBar.web.tsx). In every layout the
// app measures a clamped width (dimensions.ts), so text and spacing stay in
// phone range while the column can be wide. The ground is sampled from the
// screen itself (useScreenGround.ts).

import React, { useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native-web';

import { CANVAS_MAX_WIDTH } from './dimensions';
import { FrameContext, type Frame } from './frameContext';
import { Sidebar } from './Sidebar';
import { useLayoutMode } from './useLayoutMode';
import { isAppRoot, useRootRoute } from './useRootRoute';
import { guardInitialDeepLink } from './deepLinkGuard';
import { useScreenGround } from './useScreenGround';

// Content column widths per frame.
const TABLET_CANVAS = 600;
const AUTH_CANVAS = 640;
const APP_CANVAS = 880;

const DEFAULT_GROUND = '#F3F6F8';

export function WebShell({ children }: { children: React.ReactNode }): React.JSX.Element {
  const mode = useLayoutMode();
  const route = useRootRoute();
  const canvasRef = useRef<HTMLElement | null>(null);

  let frame: Frame;
  if (mode === 'phone') frame = 'phone';
  else if (mode === 'tablet') frame = 'tablet';
  else frame = isAppRoot(route.root) ? 'app' : 'auth';

  const ground = useScreenGround(canvasRef, `${frame}:${route.root}:${route.nested}`, DEFAULT_GROUND);

  // A typed URL or a refresh lands on that screen directly; make sure the
  // visitor is signed in before showing anything private.
  useEffect(() => guardInitialDeepLink(), []);

  // One tree for every frame. The frames differ only in styles and in
  // whether the sidebar renders; `children` (the whole app, including its
  // navigator) must never move to a different parent, because React would
  // remount it and the navigator would start over at Splash. Splash then
  // routes into the app, which switches the frame again, and so on forever.
  // The sidebar slot is always present (null when hidden) so the ground
  // keeps its position among the siblings.
  const canvasMax =
    frame === 'tablet'
      ? Math.max(TABLET_CANVAS, CANVAS_MAX_WIDTH)
      : frame === 'auth'
        ? AUTH_CANVAS
        : frame === 'app'
          ? APP_CANVAS
          : undefined;

  return (
    <FrameContext.Provider value={frame}>
      <View style={styles.appFrame}>
        {frame === 'app' ? <Sidebar route={route} /> : null}
        <View style={[styles.pageGround, { backgroundColor: ground }]}>
          <View
            ref={canvasRef}
            style={[styles.canvas, styles.pageCanvas, canvasMax !== undefined && { maxWidth: canvasMax }]}
          >
            {children}
          </View>
        </View>
      </View>
    </FrameContext.Provider>
  );
}

const styles = StyleSheet.create({
  // At least viewport-high ground, grows with content, page scroll. On the
  // phone frame the canvas is full width so the ground never shows.
  pageGround: {
    flex: 1,
    minHeight: '100vh',
    alignItems: 'center',
  },
  canvas: {
    flex: 1,
    width: '100%',
    backgroundColor: 'transparent',
    overflow: 'hidden',
  },
  pageCanvas: {
    overflow: 'visible',
    minHeight: '100vh',
  },
  appFrame: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    minHeight: '100vh',
  },
});
